import { useCallback, useEffect, useMemo, useState } from "react";
import Form from "react-bootstrap/Form";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";
import "./ManageSensor.scss";
import InventoryProfileHeader from "../../../components/inventoryprofileheader/InventoryProfileHeader";
import { useAppSelector } from "../../../redux/hooks";
import Loading from "../../../components/LoadingPage/Loading";
import React from "react";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import { currentSensor } from "../../../redux/sensor/sensorSlice";
import { Button, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ReactSVG } from "react-svg";
import editicon from "../../../assets/images/edit-icon.svg";
import starticon from "../../../assets/images/play.svg";
import stopicon from "../../../assets/images/stop-circle.svg";
import questionMarkIcon from "../../../assets/images/question-mark.svg";
import refreshIcon from "../../../assets/images/refresh-icon.svg";
import { useNavigate } from "react-router-dom";
import useSensor from "../../../Hooks/useSensor";
import moment from "moment";
import "moment-timezone";
import GraphModel from "../../../components/graphModel/graphModel";
import { toggleClass } from "../../../utils/commonUtils";
import areaChartIcon from "../../../assets/images/area_chart_icon.svg";
import reportIcon from "../../../assets/images/report-icon.svg";
import { toast } from "react-toastify";
import BatteryDisplay from "../../../components/BatteryDisplay/BatteryDisplay";
import { TemperatureLogReportModel } from "./TemperatureLogReportModel";

const tempUnit = [
  { label: "Celsius (°C)", value: "true" },
  { label: "Fahrenheit (°F)", value: "false" },
];

interface ISensorCreate {
  id?: string;
  sensorName: string;
  serialNumber: string;
  isActive: boolean;
  clientCode?: string;
  enrolled?: boolean;
  lowerTempLimit?: number | null;
  higherTempLimit?: number | null;
  intervalMinutes?: number | null;
  intervalSeconds?: number | null;
  taskId?: string | null;
  zebraId?: string | null;
  alarmActive?: boolean;
  contactNo?: string | null;
  emailId?: string | null;
  inCentigrade: string;
  sensorMACAddress: string | null;
}
const initialSensorValue = {
  sensorName: "",
  serialNumber: "",
  isActive: true,
  clientCode: "",
  enrolled: false,
  lowerTempLimit: null,
  higherTempLimit: null,
  intervalMinutes: null,
  intervalSeconds: null,
  taskId: null,
  zebraId: null,
  alarmActive: false,
  contactNo: null,
  emailId: null,
  inCentigrade: "true",
  sensorMACAddress: null,
};
const ManageSensor = () => {
  const selectedSensor = useAppSelector(currentSensor);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const navigate = useNavigate();
  const {
    getRetrieveFromZebraAPI,
    getRetrieveFromZebraLoading,
    startSensorAPI,
    startSensorLoading,
    stopSensorAPI,
    stopSensorLoading,
    editSensorAPI,
    editSensorLoading,
  } = useSensor();
  const [isUseFor, setIsUseFor] = useState<string>("view");
  const [dynamicForm, setDynamicForm] = useState<ISensorCreate>(initialSensorValue);
  const [zebraStatus, setZebraStatus] = useState<any>(null);
  const [showGraphModel, setShowGraphModel] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  /* Start the Sensor */
  const handleStartSensor = async (sensorId: string) => {
    try {
      const res = await startSensorAPI({ id: sensorId });
      if (res?.message) {
        toast.success(res.message);
      }
      setIsUseFor("view");
    } catch (err) {
      console.error("Error starting sensor:", err);
      return err;
    } finally {
      getRetrieveFromZebraData(selectedSensor?.id ?? null);
    }
  };

  /* Stop the Sensor */
  const handleStopSensor = async (sensorId: string) => {
    try {
      const res = await stopSensorAPI({ id: sensorId });
      if (res?.message) {
        toast.success(res.message);
      }
      setIsUseFor("view");
    } catch (err) {
      console.error("Error stopping sensor:", err);
      return err;
    } finally {
      getRetrieveFromZebraData(selectedSensor?.id ?? null);
    }
  };

  const handleSensorEdit = () => {
    setIsUseFor("edit");
  };

  useEffect(() => {
    if (isUseFor === "create") {
      setDynamicForm(initialSensorValue);
    } else {
      setDynamicForm({
        isActive: selectedSensor?.isActive ?? true,
        sensorName: selectedSensor?.sensorName ?? "",
        serialNumber: selectedSensor?.serialNumber ?? null,
        clientCode: selectedSensor?.clientCode ?? "",
        enrolled: selectedSensor?.enrolled ?? false,
        lowerTempLimit: selectedSensor?.lowerTempLimit ?? null,
        higherTempLimit: selectedSensor?.higherTempLimit ?? null,
        intervalMinutes: selectedSensor?.intervalMinutes ?? null,
        intervalSeconds: selectedSensor?.intervalSeconds ?? null,
        taskId: selectedSensor?.taskId ?? null,
        zebraId: selectedSensor?.zebraId ?? null,
        alarmActive: selectedSensor?.alarmActive ?? false,
        contactNo: selectedSensor?.contactNo ?? null,
        emailId: selectedSensor?.emailId ?? null,
        inCentigrade: selectedSensor?.inCentigrade.toString(),
        sensorMACAddress: selectedSensor?.sensorMACAddress ?? null,
      });
    }
  }, [isUseFor]);

  const {
    handleChange,
    values,
    handleSubmit,
    touched,
    errors
  } = useFormik({
    initialValues: dynamicForm,
    enableReinitialize: true,
    validationSchema: Yup.object({
      sensorName: Yup.string().max(256, "Sensor Name should not be more than 256 characters"),
      serialNumber: Yup.string().required("Serial Number is required"),
      manufacturer: Yup.string(),
    }),
    onSubmit: (values: any, { setSubmitting }: FormikHelpers<any>) => {
      const payload = {
        ...values,
        inCentigrade: values?.inCentigrade === "true" ? true : false,
        subscriptionEndDate: values.subscriptionEndDate,
      };
      if (isUseFor === "edit") {
        editSensorAPI({
          ...payload,
          updatedBy: currentUserId,
          id: selectedSensor?.id,
        }).then((res: any) => {
          if (res) {
            toast.success("Sensor updated successfully");
            setIsUseFor("view");
          }
        });
      }
      setSubmitting(false);
    },
    isInitialValid: isUseFor !== "create",
  });

  const getRetrieveFromZebraData = useCallback((id: string | null) => {
    if (values?.zebraId != null && isUseFor !== "create") {
      getRetrieveFromZebraAPI({ id: id }).then((res: any[]) => {
        if (res) {
          setZebraStatus(res);
        } else {
          setZebraStatus(null);
        }
      });
    }
  }, [values?.zebraId, isUseFor]);

  useEffect(() => {
    getRetrieveFromZebraData(selectedSensor?.id ?? null);
  }, [values?.zebraId, isUseFor]);

  const onCloseHandler = () => {
    navigate("/temperature-sensor");
    setDynamicForm(initialSensorValue);
  };

  const handleSensorGraph = () => {
    toggleClass();
    setShowGraphModel(!showGraphModel);
  };

  const toggleGraphModal = () => {
    toggleClass();
    setShowGraphModel(!showGraphModel);
  };

  const onShowReportModal = () => {
    toggleClass();
    setShowReportModal(true);
  };

  const onCloseReportModal = () => {
    toggleClass();
    setShowReportModal(false);
  };

  const handleRefreshTemp = () => {
    getRetrieveFromZebraData(selectedSensor?.id ?? null);
  };

  const currentTempDetails = useMemo(() => {
    if (zebraStatus?.sensors && zebraStatus?.sensors?.length > 0) {
      const data = zebraStatus?.sensors?.[0];
      return {
        temp: data?.lastUpdatedTemperature,
        date: data?.lastUpdatedTime,
        sensorStatus: data.sensorStatus,
        sensorTaskStatus: data.sensorTaskStatus,
        sensorTaskStatusColor: data.sensorTaskStatusColor,
        sensorStatusColor: data.sensorStatusColor,
        batteryLevel: data.batteryLevel
      };
    }
  }, [zebraStatus?.sensors]);

  return (
    <>
      <div className="manage-sensor-wrapper">
        {startSensorLoading || stopSensorLoading || getRetrieveFromZebraLoading ? (
          <Loading />
        ) : null}
        <div className="section-header">
          <InventoryProfileHeader
            status={selectedSensor?.isActive || values.isActive}
            itemName={selectedSensor?.serialNumber || values.serialNumber}
          />
          <div className="inventory-actions-box">
            <span
              className="invt-action-btn envt-edit-btn me-2"
              onClick={handleSensorEdit}
            >
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Edit Sensor</Tooltip>}
                trigger={["hover", "focus"]}
              >
                <ReactSVG className="svg-box" wrapper="span" src={editicon} />
              </OverlayTrigger>
            </span>{" "}
            {selectedSensor && selectedSensor?.taskId == null ? (
              <span
                className="invt-action-btn envt-edit-btn me-2"
                onClick={() => handleStartSensor(selectedSensor?.id || "")}
              >
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip>Start Sensor</Tooltip>}
                  trigger={["hover", "focus"]}
                >
                  <ReactSVG
                    className="svg-box"
                    wrapper="span"
                    src={starticon}
                  />
                </OverlayTrigger>
              </span>
            ) : (
              <span
                className="invt-action-btn envt-edit-btn me-2"
                onClick={() => handleStopSensor(selectedSensor?.id || "")}
              >
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip>Stop Sensor</Tooltip>}
                  trigger={["hover", "focus"]}
                >
                  <ReactSVG className="svg-box" wrapper="span" src={stopicon} />
                </OverlayTrigger>
              </span>
            )}
            <span
              className="invt-action-btn envt-edit-btn me-2"
              onClick={() => handleSensorGraph()}
            >
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Temperature Graph</Tooltip>}
                trigger={["hover", "focus"]}
              >
                <ReactSVG
                  className="svg-box"
                  wrapper="span"
                  src={areaChartIcon}
                />
              </OverlayTrigger>
            </span>
            <span
              className="invt-action-btn envt-edit-btn me-2"
              onClick={onShowReportModal}
            >
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Temperature Log Report</Tooltip>}
                trigger={["hover", "focus"]}
              >
                <ReactSVG
                  className="svg-box"
                  wrapper="span"
                  src={reportIcon}
                />
              </OverlayTrigger>
            </span>
            {currentTempDetails && (<span
              className="invt-action-btn envt-edit-btn me-2"
            >
              <BatteryDisplay level={currentTempDetails?.batteryLevel ?? 0} />
            </span>)}
          </div>
        </div>
        <Form onSubmit={handleSubmit} className="manage-sensor-form">
          <div className="details-box">
            <div className="form-fields-grid">
              <Form.Group className="form-field">
                <Form.Label>Serial Number*</Form.Label>
                <Form.Control
                  id="serialNumber"
                  name="serialNumber"
                  isInvalid={touched.serialNumber && !!errors.serialNumber}
                  type="text"
                  placeholder="Serial Number"
                  onChange={handleChange}
                  value={values.serialNumber}
                  disabled={true}
                />
                {touched.serialNumber && errors.serialNumber ? (
                  <Form.Control.Feedback type="invalid">
                    {errors.serialNumber}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>
              <Form.Group className="form-field">
                <Form.Label>Active </Form.Label>
                <Form.Check
                  type="switch"
                  id="isActive"
                  name="isActive"
                  onChange={handleChange}
                  checked={values.isActive}
                  disabled={true}
                />
              </Form.Group>
              {currentTempDetails && (
                <Form.Group style={{ textAlign: "right" }}>
                  <h3>{currentTempDetails.temp}</h3>
                  <p style={{ margin: 0, lineHeight: "1.5" }}>
                    {currentTempDetails.date}{" "}
                    <span
                      className="invt-action-btn envt-edit-btn"
                      onClick={() => handleRefreshTemp()}
                    >
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip>Refresh Temperature</Tooltip>}
                        trigger={["hover", "focus"]}
                      >
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={refreshIcon}
                        />
                      </OverlayTrigger>
                    </span>
                  </p>
                  <p style={{ margin: 0 }}>
                    Status:{" "}
                    <span
                      style={{
                        fontWeight: "bold",
                        color: currentTempDetails?.sensorTaskStatusColor
                          ? currentTempDetails.sensorTaskStatusColor.toLowerCase()
                          : undefined,
                      }}
                    >
                      {currentTempDetails?.sensorTaskStatus}
                    </span>
                  </p>
                </Form.Group>
              )}
            </div>
          </div>

          {isUseFor !== "create" && (
            <>
              <div className="details-box">
                <p className="form-section-title">Configrations</p>
                <div className="form-fields-grid">
                  <Form.Group className="form-field">
                    <Form.Label>Sensor Name</Form.Label>
                    <Form.Control
                      id="sensorName"
                      name="sensorName"
                      type="text"
                      placeholder="Sensor Name"
                      onChange={handleChange}
                      value={values.sensorName}
                      isInvalid={touched.sensorName && !!errors.sensorName}
                      disabled={isUseFor === "view"}
                    />
                    {touched.sensorName && errors.sensorName ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.sensorName}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  {/* <Form.Group className="form-field">
                    <Form.Label>Client ID</Form.Label>
                    <Form.Control
                      id="clientCode"
                      name="clientCode"
                      onChange={handleChange}
                      value={values.clientCode}
                      isInvalid={touched.clientCode && !!errors.clientCode}
                      required
                      type="text"
                      placeholder="Client ID"
                      disabled={true}
                    />
                    {touched.clientCode && errors.clientCode ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.clientCode}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="form-field">
                    <Form.Label>Zebra Id</Form.Label>
                    <Form.Control
                      id="zebraId"
                      name="zebraId"
                      onChange={handleChange}
                      value={values?.zebraId ?? ""}
                      isInvalid={touched.zebraId && !!errors.zebraId}
                      type="text"
                      placeholder="Zebra ID"
                      required
                      disabled={true}
                    />
                    {touched.zebraId && errors.zebraId ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.zebraId}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="form-field">
                    <Form.Label>Task ID</Form.Label>
                    <Form.Control
                      id="taskId"
                      name="taskId"
                      onChange={handleChange}
                      value={values?.taskId ?? ""}
                      isInvalid={touched.taskId && !!errors.taskId}
                      type="text"
                      placeholder="Task ID"
                      required
                      disabled={true}
                    />
                    {touched.taskId && errors.taskId ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.taskId}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group> */}
                  <Form.Group className="form-field">
                    <Form.Label>Lower Temp Limit</Form.Label>
                    <Form.Control
                      id="lowerTempLimit"
                      name="lowerTempLimit"
                      onChange={handleChange}
                      value={values?.lowerTempLimit ?? ""}
                      isInvalid={
                        touched.lowerTempLimit && !!errors.lowerTempLimit
                      }
                      type="number"
                      placeholder="Lower Temp Limit"
                      disabled={isUseFor === "view" || Boolean(values.taskId)}
                    />
                    {touched.lowerTempLimit && errors.lowerTempLimit ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.lowerTempLimit}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="form-field">
                    <Form.Label>High Temp Limit</Form.Label>
                    <Form.Control
                      id="higherTempLimit"
                      name="higherTempLimit"
                      onChange={handleChange}
                      value={values?.higherTempLimit ?? ""}
                      isInvalid={
                        touched.higherTempLimit && !!errors.higherTempLimit
                      }
                      type="number"
                      placeholder="Higher Temp Limit"
                      required
                      disabled={isUseFor === "view" || Boolean(values.taskId)}
                    />
                    {touched.higherTempLimit && errors.higherTempLimit ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.higherTempLimit}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="form-field">
                    <Form.Label>Interval Minutes</Form.Label>
                    <Form.Control
                      id="intervalMinutes"
                      name="intervalMinutes"
                      onChange={handleChange}
                      value={values?.intervalMinutes ?? ""}
                      isInvalid={
                        touched.intervalMinutes && !!errors.intervalMinutes
                      }
                      type="number"
                      placeholder="Interval Minutes"
                      required
                      disabled={isUseFor === "view" || Boolean(values.taskId)}
                    />
                    {touched.intervalMinutes && errors.intervalMinutes ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.intervalMinutes}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="form-field">
                    <Form.Label>Interval Seconds</Form.Label>
                    <Form.Control
                      id="intervalSeconds"
                      name="intervalSeconds"
                      onChange={handleChange}
                      value={values?.intervalSeconds ?? ""}
                      isInvalid={
                        touched.intervalSeconds && !!errors.intervalSeconds
                      }
                      type="number"
                      placeholder="Interval Seconds"
                      required
                      disabled={isUseFor === "view" || Boolean(values.taskId)}
                    />
                    {touched.intervalSeconds && errors.intervalSeconds ? (
                      <Form.Control.Feedback type="invalid">
                        {errors.intervalSeconds}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="form-field">
                    <Form.Label>Temp Unit</Form.Label>
                    <Form.Control
                      as="select"
                      aria-label="inCentigrade"
                      id="inCentigrade"
                      name="inCentigrade"
                      onChange={handleChange}
                      value={values.inCentigrade}
                      isInvalid={touched.inCentigrade && !!errors.inCentigrade}
                      disabled={true}
                    >
                      <option>Select Unit</option>
                      {tempUnit.map((role: any) => (
                        <option value={role.value} key={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </Form.Control>
                    {touched.inCentigrade && errors.inCentigrade ? (
                      <Form.Control.Feedback type="invalid">
                        {(errors as any).inCentigrade}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="form-field multiple">
                    {/* <Form.Group>
                      <Form.Label>Entrolled </Form.Label>
                      <Form.Check
                        type="switch"
                        id="enrolled"
                        name="enrolled"
                        onChange={handleChange}
                        checked={values.enrolled}
                        disabled={true}
                      />
                    </Form.Group> */}
                    <Form.Group>
                      <Form.Label>Alarm </Form.Label>
                      <Form.Check
                        type="switch"
                        id="alarmActive"
                        name="alarmActive"
                        onChange={handleChange}
                        checked={values.alarmActive}
                        disabled={isUseFor === "view"}
                      />
                    </Form.Group>
                  </Form.Group>
                </div>
              </div>
              <div className="details-box">
                <p className="form-section-title">Alarm Configrations</p>
                <div className="form-fields-grid">
                  <Form.Group className="form-field">
                    <Form.Label>
                      Alarm Phone Number{" "}
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={
                          <Tooltip>
                            Please enter multiple numbers separated by commas
                          </Tooltip>
                        }
                        trigger={["hover", "focus"]}
                      >
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={questionMarkIcon}
                        />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      id="contactNo"
                      name="contactNo"
                      type="text"
                      placeholder="Enter Phone number"
                      onChange={handleChange}
                      value={values?.contactNo ?? ""}
                      disabled={isUseFor === "view" || !values?.alarmActive}
                    />
                  </Form.Group>
                  <Form.Group className="form-field">
                    <Form.Label>
                      Alarm Email ID{" "}
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={
                          <Tooltip>
                            Please enter multiple email ids separated by commas
                          </Tooltip>
                        }
                        trigger={["hover", "focus"]}
                      >
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={questionMarkIcon}
                        />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      id="emailId"
                      name="emailId"
                      type="text"
                      placeholder="Enter Email ID"
                      onChange={handleChange}
                      value={values.emailId ?? ""}
                      disabled={isUseFor === "view" || !values?.alarmActive}
                    />
                  </Form.Group>
                </div>
              </div>
            </>
          )}
          <div className="form-actions">
            <Button variant="outline-primary" onClick={onCloseHandler}>
              Close
            </Button>
            {isUseFor === "edit" ? (
              <Button
                variant="primary"
                type="submit"
                onClick={handleSubmit as any}
              >
                Update
              </Button>
            ) : null}
          </div>
        </Form >
      </div >
      {editSensorLoading && <Loading />}
      {
        showGraphModel && (
          <GraphModel
            showModal={showGraphModel}
            toggleModal={toggleGraphModal}
            modelName={`Temperature Log`}
            sensorData={
              selectedSensor?.id
                ? {
                  id: selectedSensor.id,
                  inCentigrade: selectedSensor.inCentigrade === "true",
                }
                : null
            }
          />
        )
      }
      {showReportModal && (
        <TemperatureLogReportModel
          showReportModal={showReportModal}
          onCloseReportModal={onCloseReportModal}
          selectedSensor={
            selectedSensor?.id
              ? {
                  id: selectedSensor.id,
                  serialNumber: selectedSensor.serialNumber,
                  sensorName: selectedSensor.sensorName,
                  inCentigrade: selectedSensor.inCentigrade === "true",
                  lowerTempLimit: selectedSensor.lowerTempLimit,
                  higherTempLimit: selectedSensor.higherTempLimit,
                }
              : null
          }
        />
      )}
    </>
  );
};

export default ManageSensor;
